import React from 'react';
import axios from 'axios';
import { useState, useEffect, useRef } from "react";
import { useWindowSize } from 'react-use';
import { useSessionStorageReducer } from "./useSessionStorageReducer";
import MyMap from './MapSearch/MyMap';
import ManagementPanelReport from './MapSearch/ManagementPanelReport';
import ActiveDataPanelReport from './MapSearch/ActiveDataPanelReport';
import MessageDialog from './MapSearch/MessageDialog';
import DeleteDialog from './MapSearch/DeleteDialog';
import fitBounds from './MapSearch/MyMapUtils/fitBounds';
import blobPath from './MapSearch/MyMapUtils/blobPath';

const activeImgMaxHeight = 300;
const pcModeMinWidth = 480;
const mapBoundN = 118;  //地図の上部マージン
const mapBoundE = 100;   //地図の右部マージン
const mapBoundS = 0;  //地図の下部マージン
const mapBoundW = 38;   //地図の左部マージン
//Mapのサイズ
const containerStyle = {
    width: "100%",
    height: "calc(100vh - 50px)"
};
const defaultMapServerType = "GoogleMap";
const defaultLocation = [36.557522, 136.875558]; // 南砺市役所

export default function ReportManagement() {

    // レイアウト関連 //
    const { width, height } = useWindowSize();  //Windowサイズ
    //画面右側（地図、ActiveDataPanelの親ContentStyle）
    function getContainerStyle() {
        return {
            width: width <= 1280 ? "calc(100% - 640px)" : width / 2  > 700 ? "calc(100% - 700px)": `calc(100% - ${width / 2}px)`,
            height:  "calc(100vh - 50px)",
            float:"right",
            display:"flex",
            flexDirection:"column"
        };
    }
    //GoogleMap
    const [map, setMap] = useState(null);
    //メッセージダイアログ
    const [MessageDialogLabel, setMessageDialogLabel] = useState("");  //ラベル
    const [MessageDialogMessage, setMessageDialogMessage] = useState("");  //メッセージ

    // 状態関連 //
    //詳細パネルの表示状態
    const [showActiveDataPanel, setShowActiveDataPanel] = useState(false);
    //ダイアログの表示状態（いずれかのダイアログが表示されている場合は、表示）
    const [showDialog, setShowDialog] = useState(false);
    //削除ダイアログの表示状態
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    //メッセージ/プログレスダイアログの表示状態 
    const [showMessageDialog, setShowMessageDialog] = useState(false);
    useEffect(() => {
        if(showDeleteDialog ||showMessageDialog){
            setShowDialog(true);
        }else{
            setShowDialog(false);
        }
    }, [showDeleteDialog,showMessageDialog]); //ダイアログの表示状態が変わった時
    //モバイルモード
    const isMobileMode = width < pcModeMinWidth;
    //検索モード
    const [isSearching, setIsSearching] = useState(false);
    //編集モード
    const [isEditMode, setIsEditMode] = useState(false);
    //編集されたか
    const [isEdit, setIsEdit] = useState(false);
    //更新モード
    const [isUpdate, setIsUpdate] = useState(false);
    //プログレス状態
    const [progressType, setProgressType] = useState("LinearIndeterminate");
    
    // データ関連 //
    //フィルタ
    const [filters, updateFilter] = useSessionStorageReducer("report_filters", {
        keyword: "",
        reportStatus:"",
        searchScope:false,
        reportDate_str:"",
        reportDate_end:"",
    });
    const [sortType,updateSortType] = useState("Report.reportDate desc");
    //検索結果のItems
    const [items, setItems] = useState(window.SIMPL.items);
    //Itemsの数
    const [resultCount, setResultCount] = useState(window.SIMPL.items?.length ? window.SIMPL.items?.length : 0);
    //検索結果一覧でhoer状態のItem（地図でのアニメーション用）
    const [activeItem, setActiveItem] = useState(null);
    const [activeImg, setActiveImg] = useState(null);
    //選択中のItem
    const [selectedItem, setSelectedItem] = useState(null);
    //旧選択中のItem
    const [oldSelectedItem, setOldSelectedItem] = useState(null);
    //削除Items
    const [removeItems, setRemoveItems] = useState([]);

    // その他 //
    //編集前の経度　保持
    const [editSelectItem_lng, setEditSelectItem_lng] = useState(null);
    //編集前の緯度　保持
    const [editSelectItem_lat, setEditSelectItem_lat] = useState(null);
    //緯度経度の編集（レイアウトをちゃんと適用する為に用意）
    const [isEditLocationAngle, setIsEditLocationAngle] = useState(false);
    //位置情報の編集用ref
    //（ドラッグ・クリックでの編集でもテキストへ即反映する為）
    const startLatRef = useRef(null);
    const startLngRef = useRef(null);
    //地図の表示範囲
    const [searchScopeString, setSearchScopeString] = useState(null);

    //プログレスバー
    const [progress, setProgress] = useState(0);

    // マップ関連
    function onInitMap(map) {
        setMap(map);
    }
    function onIdle() {
        setSearchScopeString(getBoundsLatLngString(map));
    }
    function getBoundsLatLngString(map) {
        let eastLng = map.getBounds()?.getNorthEast()?.lng();
        let southLat = map.getBounds()?.getSouthWest()?.lat();
    
        let northLat = map.getBounds()?.getNorthEast()?.lat();
        let westLng = map.getBounds()?.getSouthWest()?.lng();
    
        return `[[${westLng},${northLat}],[${westLng},${southLat}],[${eastLng},${southLat}],[${eastLng},${northLat}],[${westLng},${northLat}]]`;
    }

    //検索条件文字列の取得
    function getSearchString(){
        const keyword = filters.keyword ? encodeURIComponent(filters.keyword) : "";
        const reportStatus = filters.reportStatus  ? encodeURIComponent(filters.reportStatus) : "";
        const searchScope = filters.searchScope ? encodeURIComponent(searchScopeString) : "";
        const reportDate_str = filters.reportDate_str ? encodeURIComponent(filters.reportDate_str) : "";
        const reportDate_end = filters.reportDate_end ? encodeURIComponent(filters.reportDate_end) : "";
        
        return `?keyword=${keyword}&reportStatus=${reportStatus}&searchScope=${searchScope}&reportDate_str=${reportDate_str}&reportDate_end=${reportDate_end}`;
    }
    //検索処理
    async function search(mode,st) {
        setIsSearching(true);   //検索開始
        const sort = st ? encodeURIComponent(st):encodeURIComponent(sortType);  //ソート情報の調整
        let url = `/Management/ReportManagementSearch${getSearchString()}&sort=${sort}`;
        console.log(url);

        fetch(url)
        .then(response => response.json())
        .then(
            (result) => {
                console.log(result);
                switchSearch(result,mode);
            },
            (error) => {
                console.log(error);
            }
        )
        .finally(() => {
            setIsSearching(false);
        });
    }
    function switchSearch(result,mode){
        //共通処理
        var itemlist = result;
        if(Object.keys(result).length !== 0){
            itemlist = result.slice(0,100);
        }
        setItems(itemlist);   //一覧データの更新
        setResultCount(result.length);
        resetCheckBox();    //チェック状態のリセット 

        switch (mode) {
            default:
            case 'search':
            case 'upload':
            case 'delete':
                //選択Itemの解除
                setShowActiveDataPanel(false);
                setSelectedItem(null); 
                
                //（緯度経度が0,0は除外）
                if(Object.keys(result).length !== 0){
                    var list = result.filter(item => item.startLat !== 0 || item.startLng !== 0);
                    fitBounds(map,list,false,mapBoundN,mapBoundE,mapBoundS,mapBoundW);
                }
                break;
        }
    }
    //データ更新処理
    function updateData(data,fixReportStatus,fixComment,fixMemo){
        setIsUpdate(true);

        //緯度経度が編集されているか
        let lat = data["Location"]["coordinates"][1] !== editSelectItem_lat;
        let lng = data["Location"]["coordinates"][0] !== editSelectItem_lng;

        let locationSettingFlg;
        //編集があった場合、次の条件へ
        //編集がない場合、元の設定値のまま
        if(lng || lat){
            //オリジナル位置情報が未設定なら、次の条件へ
            //オリジナル位置情報が設定済みなら、編集があったとしてtrue
            if(!data["originalDataLocationSettingFlg"]){
                
                let lat2 = data["Location"]["coordinates"][1] !== defaultLocation[0];
                let lng2 = data["Location"]["coordinates"][0] !== defaultLocation[1];
                //デフォルト値以外ならtrue
                //デフォルト値ならfalse
                if(lat2 || lng2){
                    locationSettingFlg = true;
                }else{
                    locationSettingFlg = false;
                }
            }else{
                locationSettingFlg =true;
            }
        }else{
            locationSettingFlg = data["locationSettingFlg"];
        }

        var now = new Date();
        var Year = now.getFullYear();
        var Month = now.getMonth()+1;
        var day = now.getDate();
        var Hour = now.getHours();
        var Min = now.getMinutes();
        var Sec = now.getSeconds();
        let modifiedDateTime = Year + "-" + Month + "-" + day + " " + Hour + ":" + Min + ":" + Sec;
        let modifiedDateTime2 = Year + "-" + Month + "-" + day + " " + Hour + ":" + Min;
        let modifiedBy = window.SIMPL.userAccount;
        var sendData = {
            id:data["id"],
            Location:data["Location"],
            locationSettingFlg:locationSettingFlg,
            reportStatus:fixReportStatus,
            comment:fixComment,
            memo:fixMemo,
            modifiedDateTime:modifiedDateTime,
            modifiedBy:modifiedBy
        }

        axios.post(`/Management/ReportManagementUpdate`, JSON.stringify(sendData))
        .then()
        .catch(function (error) {
            console.log(error);
        })
        .finally(() => {
            
            //更新後、再検索ではなく、ローカルの一覧データの対象データを更新して表示。
            console.log(items);
            var target = items.find(item => item.id === selectedItem.id);
            target.Location.coordinates[0] = sendData.Location["coordinates"][0];
            target.Location.coordinates[1] = sendData.Location["coordinates"][1];
            target.locationSettingFlg = sendData.locationSettingFlg;
            target.reportStatus = sendData.reportStatus;
            target.comment = sendData.comment;
            target.memo = sendData.memo;
            target.modifiedDateTime = modifiedDateTime2;
            target.modifiedBy = sendData.modifiedBy;
            console.log(target);
            console.log(items);

            setIsEditMode(false);   //編集モードの解除
            setIsEdit(false);
            setIsUpdate(false); //アップデートモードの解除
        });
    }
    //削除処理
    async function deleteData(){
        axios.post(`/Management/ReportManagementDelete`, JSON.stringify(removeItems))
        .then(
            (result) => {
                //削除完了後のメッセージダイアログラベル・メッセージ・表示　更新
                setMessageDialogLabel("削除が完了しました。");
                setMessageDialogMessage("※地図検索から削除されるまで、少し時間がかかる場合があります。");
                setShowMessageDialog(true); 
                //削除ダイアログ　非表示
                setShowDeleteDialog(false);
            },
            (error) => {
                console.log(error);
            }
        )
        .catch(function (error) {
            console.log(error);
        })
        .finally(() => {
            setIsEditMode(false);   //編集モードの解除
            setIsEdit(false);
            setIsUpdate(false); //アップデートモードの解除

            search("delete");   //削除後の再検索
        });
    }

    //[クリア]ボタン押下処理
    function onClearClick() {
        updateFilter({type:"keyword",value:""});
        updateFilter({type:"reportStatus",value:""});
        updateFilter({type:"searchScope",value:false});
        updateFilter({type:"reportDate_str",value:""});
        updateFilter({type:"reportDate_end",value:""});
    }

    //選択行の変更+編集内容の削除処理
    function onChangeSelectedRowAndEditClear(itemData,e,isFitBounds = true,isScroll = true){
        //編集内容のクリア
        var isClear = editClear();
        if(isClear){
            //選択行の変更
            var isChangeLayout = onChangeSelectedRow(itemData,e);
            //地図の表示位置変更
            if(isChangeLayout && isFitBounds)fitBounds(map,null,new window.google.maps.LatLng(itemData.startLat, itemData.startLng),mapBoundN,mapBoundE,mapBoundS,showActiveDataPanel || selectedItem === null ? mapBoundW + 320 : mapBoundW);
            //スクロール位置の変更
            if(isScroll)document.getElementById(itemData.id).scrollIntoView(true);
        }
    }
    //選択データ(行)変更処理
    function onChangeSelectedRow(itemData,e){
        //行の選択で変更されている場合のみ
        if(e !== null && e !== undefined){
            //チェックボックス列がクリックされた場合選択行の変更はしない
            if(e.target.type === "checkbox"
                ||e.target.className === "Result_checkBox"
                ||e.target.type === "submit"){
                return false;
            }
        }
        //選択行以外のチェックの解除+選択行のチェック状態の変更
        var targetRow = document.getElementById(itemData.id);
        if(targetRow.children[0].children[0].type === "checkbox"){
            if((selectedItem !==null && selectedItem.id === itemData.id)){
                setShowActiveDataPanel(false);
                setSelectedItem(null);
                return false;
            }else{
                setSelectedItem(itemData);
                setShowActiveDataPanel(true);
                //編集前状態の保持
                setEditSelectItem_lng(itemData.startLng);
                setEditSelectItem_lat(itemData.startLat);
                return true;
            }
        }
    }
   //全チェックボックス選択操作
   function onChangeRemoveAllCheck(e,val) {
    let checkbox_list = document.querySelectorAll(".Result_checkBox_box");
    (checkbox_list).forEach(checkbox => {
        checkbox.checked = e.target.checked;
    });
    setRemoveItems(e.target.checked? val :[]);
}
    //チェックボックス選択操作
    function onChangeRemoveCheck(e,val) {
        //チェックボックスが押された場合
        if(e.target.type === "checkbox"){
            if(e.target.checked){
                setRemoveItems([...removeItems.concat(val)]);
            }else{
                setRemoveItems([...removeItems.filter((item)=>item !== val)]);
            }
        }
        //チェックボックス列が押された場合
        else{
            if(!e.target.children[0].checked){
                e.target.children[0].checked = true;
                setRemoveItems([...removeItems.concat(val)]);
            }else{
                e.target.children[0].checked = false;
                setRemoveItems([...removeItems.filter((item)=>item !== val)]);
            }
        }
        
    }
    //全てのチェックボックス状態のリセット+削除Itemの解除
    function resetCheckBox(){
        let checkbox_all = document.getElementById("allCheck");
        checkbox_all.checked = false;
        let checkbox_list = document.querySelectorAll(".Result_checkBox_box");
        (checkbox_list).forEach(checkbox => {
            checkbox.checked = false;
        });
        setRemoveItems([]);
    }

    //ソート種類の変更処理
    function onChangeSortType(val){
        updateSortType(val);
        search("search",val);
    }

    //位置情報編集（マーカードラッグ後の処理）
    function onDragendMarker(target){
        let lat = target.getPosition().lat();
        let lng = target.getPosition().lng();
        var lat2 = Math.round(lat * 1000000) / 1000000;   //桁の調整
        var lng2 = Math.round(lng * 1000000) / 1000000;   //桁の調整

        let si = selectedItem;
        si.startLat = lat2;
        si.startLng = lng2;
        si.Location.coordinates = [lng2,lat2];
        setSelectedItem(si);
        startLatRef.current.value = lat2;
        startLngRef.current.value = lng2;
        setIsEditLocationAngle(!isEditLocationAngle);
        setIsEdit(true);
    }

    //編集内容のクリア
    function editClear(isCancel = false){
        if(isEditMode){
            if(isEdit){
                var result = window.confirm("選択された住民通報情報が編集中です。編集を破棄しますか？");
                if(result === false)return false;
    
                var si = selectedItem;
                si.startLat = editSelectItem_lat;
                si.startLng = editSelectItem_lng;
                si.Location.coordinates = [editSelectItem_lng,editSelectItem_lat];
                setSelectedItem(si);
                setIsEdit(false);
                
                fitBounds(map,null,new window.google.maps.LatLng(editSelectItem_lat, editSelectItem_lng),mapBoundN,mapBoundE,mapBoundS,showActiveDataPanel || selectedItem === null ? mapBoundW + 320 : mapBoundW);
            }
            ChangeEditMode(false);
        }
        if(isCancel){
            setOldSelectedItem(selectedItem);   //旧選択行の設定
            setShowActiveDataPanel(false);
            setSelectedItem(null);
        }
        return true;
    }
    //メッセージダイアログを閉じる
    function onCloseMessageDialog(){
        setShowMessageDialog(false);
        setProgress(0);
    }
    function onEnterItem(item){
        setActiveItem(item);
        setOldSelectedItem(null);
    }
    function onEnterImg(e, data_PhotoURL) {
        if (data_PhotoURL) {
            let bounds = e.target.getBoundingClientRect();
            let top = bounds.top + (bounds.bottom - bounds.top) * 0.5 - activeImgMaxHeight * 0.5;
            if (top + activeImgMaxHeight > height) {
                top = height - activeImgMaxHeight;
            }
            setActiveImg({ top: top, left: bounds.left + 16, PhotoURL: data_PhotoURL });
        }
    }
    
    function onTilesLoaded(target){
    }

    function ChangeEditMode(isEdit){
        if(isEdit){

            //編集前状態の保持
            setEditSelectItem_lng(selectedItem.startLng);
            setEditSelectItem_lat(selectedItem.startLat);
            setIsEditMode(true);
        }else{

            //編集前状態の破棄
            setEditSelectItem_lng(null);
            setEditSelectItem_lat(null);
            setIsEditMode(false);
        }
    }
    
    return (
        <div>
            <div className="container-fluid body-content" style={{ padding: 0 }}>
                <div>
                    {/* ダイアログ関連 */}
                    {showDialog && 
                        <div style={{height:'calc(100vh - 50px)',position:"absolute",inset:"0",top:"50px"}}>
                            <div className="Dialog-back"></div> {/* ダイアログ背景 */}
                            {showDeleteDialog &&
                                <DeleteDialog    //削除ダイアログ
                                    //ダイアログを閉じた時の処理([キャンセル]ボタン押下)
                                    onBack={()=>setShowDeleteDialog(false)}
                                    //削除ダイアログの表示状態
                                    showDeleteDialog={showDeleteDialog}
                                    //削除データの件数
                                    deleatItemCount={removeItems?.length}
                                    //削除処理
                                    deleteData={deleteData}
                                />
                            }
                            {showMessageDialog &&
                                <MessageDialog    //メッセージダイアログ
                                    //ダイアログを閉じた時の処理([OK]ボタン押下)
                                    onCloseMessageDialog={onCloseMessageDialog}
                                    //メッセージダイアログの表示状態
                                    showMessageDialog={showMessageDialog}
                                    //ラベル(DialogTitle)
                                    label={MessageDialogLabel}
                                    //メッセージ(赤文字の注意書き)
                                    Message={MessageDialogMessage}
                                    //プログレスの％
                                    progress={progress}
                                    //プログレスバーのタイプ
                                    progressType={progressType}
                                />
                            }
                        </div>
                    }
                    {/* 左側のパネル関連 */}
                    <div style={{ float: 'left',height: 'calc(100vh - 50px)'}}>
                        <ManagementPanelReport    //管理パネル
                            isMobileMode={isMobileMode}
                            width={width}
                            height={height}
                            //更新モード
                            isUpdate={isUpdate}
                            //検索モード
                            isSearching={isSearching}
                            //[削除]ボタン押下(削除ダイアログの表示) 
                            setShowDeleteDialog={()=>{setShowDeleteDialog(true)}}
                            filters={filters}
                            //各フィルタの変更
                            onChangeFilter={(type,val)=> updateFilter({type:type,value:val})}
                            //検索処理
                            search={search}
                            //ソート変換処理
                            onChangeSortType={onChangeSortType}
                            //フィルタのクリア
                            onClearClick={onClearClick}
                            items={items}
                            //検索結果Item数
                            resultCount={resultCount}
                            //削除Items
                            removeItems={removeItems}
                            //選択Item
                            selectedItem={selectedItem}
                            //旧選択Item
                            oldSelectedItem={oldSelectedItem}
                            //選択行の変更処理
                            onChangeSelectedRowAndEditClear={onChangeSelectedRowAndEditClear}
                            //フォーカスがあるItem
                            activeItem={activeItem}
                            //Itemにフォーカスが当たった時の処理
                            onEnterItem={()=>onEnterItem()}
                            //Itemからフォーカスが外れた時の処理
                            onLeaveItem={() => setActiveItem(null)}
                            //チェックボックスの切替処理
                            onChangeRemoveCheck={onChangeRemoveCheck}
                            //全チェックボックスの切替処理
                            onChangeRemoveAllCheck={onChangeRemoveAllCheck}

                        />
                    </div>
                    <div className="rightArea" style={getContainerStyle()}>
                        <MyMap  //地図
                            //地図の表示Type
                            mapType={"roadmap"}
                            //地図のナビゲーション位置
                            ControlPosition={"RIGHT_BOTTOM"}
                            //地図のStyle
                            containerStyle={containerStyle}
                            //地図の初期化処理
                            onInitMap={onInitMap}
                            //地図が動いていない時の処理
                            onIdle={onIdle}
                            //地図のクリックが可能か(角度の変更は不可)
                            isClick={false}
                            //マーカークリックでinfoWindowが表示されるか(非表示)
                            showInfoWindow={false}
                            //選択行の変更処理(マーカークリック時の処理)
                            onChangeSelectedRowAndEditClear={onChangeSelectedRowAndEditClear}
                            //マーカーのドラッグが可能か(編集モードでは可能)
                            isDraggable={isEditMode}
                            //マーカーのドラッグが終わった時の処理
                            onDragendMarker={onDragendMarker}
                            //地図上に表示するマーカーのitems
                            items={items}
                            //フォーカスがあるItem
                            activeItem={selectedItem}
                            //選択Item
                            selectedItem={selectedItem}
                            onTilesLoaded={onTilesLoaded}
                            
                            mapServerType={defaultMapServerType}
                        />
                        { isEditMode && //地図上での緯度経度の編集方法パネル
                            <div  >
                                <div className="editInfo" style={width <= 1280 ? {left:`970px`} 
                                    : width / 2  > 700 ? {left:`1030px`} 
                                    : {left:`${width / 2 + 320+10}px`}}>
                                    <p>マーカーをドラッグ：位置を変更</p>
                                </div>
                            </div>
                        }
                        { showActiveDataPanel &&
                            <ActiveDataPanelReport    //ActiveDataPanel
                                height={height}
                                width={width}
                                //表示状態
                                showActiveDataPanel={showActiveDataPanel}
                                //地図
                                map={map}
                                mapBoundN={mapBoundN}
                                mapBoundE={mapBoundE}
                                mapBoundS={mapBoundS}
                                mapBoundW={mapBoundW}
                                //位置情報編集用ref
                                startLatRef={startLatRef}
                                startLngRef={startLngRef}
                                defaultLocation={defaultLocation}
                                //編集モード
                                isEditMode={isEditMode}
                                //[編集]ボタン押下(編集モードの開始)
                                setIsEditMode={()=>ChangeEditMode(true)}
                                //編集有無
                                setIsEdit={setIsEdit}
                                //更新モード
                                isUpdate={isUpdate}
                                //更新処理
                                updateData={updateData}
                                //編集内容のクリア処理
                                editClear={editClear}
                                //[削除]ボタン押下(削除ダイアログの表示)
                                setShowDeleteDialog={()=>{setShowDeleteDialog(true)}}
                                //Items
                                items={items}
                                //選択Item
                                selectedItem={selectedItem}
                                //選択Itemの変更処理
                                setSelectedItem={setSelectedItem}
                                //選択行の変更処理
                                onChangeSelectedRowAndEditClear={onChangeSelectedRowAndEditClear}
                                //削除Itemの変更処理
                                setRemoveItems={setRemoveItems}
                                //レイアウト変更用
                                isEditLocationAngle={isEditLocationAngle}
                                setIsEditLocationAngle={setIsEditLocationAngle}
                                
                                onEnterImg={onEnterImg}
                                onLeaveImg={() => setActiveImg(null)}
                            />
                        }
                        {activeImg &&
                            <img decoding="async" className="sr-item-img-active"
                                style={{ top: activeImg.top, left: activeImg.left }}
                                src={blobPath(activeImg?.PhotoURL)}
                                alt="" />
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}

